/**
 * Assessment Domain Configuration
 *
 * Maps Cito LVS domains to app modules and defines sub-test structure.
 * Based on ASSESSMENT_RESTRUCTURING_PLAN.md v3.0 §1 and §2.
 *
 * CRITICAL: ModuleId values must match TypeScript enum values exactly:
 * 'Getalbegrip', 'Basisrekenen', 'Metriek', 'Tijd', 'Geld',
 * 'SpellingRegel', 'SpellingWW', 'Taalbegrip', 'Studievaardig', 'Topo'
 */

import type { ModuleId } from '../../content'
import type { GroupId } from '../../config/systemConfig'
import type { AssessmentDomainId, SubTestDefinition } from '../types'

// ── Domain → Module Mapping ─────────────────────────────────────────────────

/** Which app modules belong to each assessment domain */
export const DOMAIN_MODULE_MAP: Record<AssessmentDomainId, ModuleId[]> = {
  rekenen: ['Getalbegrip', 'Basisrekenen', 'Metriek', 'Tijd', 'Geld'],
  taalverzorging: ['SpellingRegel', 'SpellingWW', 'Taalbegrip'],
  'begrijpend-lezen': ['Taalbegrip'],
}

/**
 * For Taalverzorging, we include TAA but only the ontleden + woordsoorten families.
 * For Begrijpend Lezen, we include TAA but only the lezen + woordenschat families.
 *
 * Since both use 'Taalbegrip' module, the blueprint generator's moduleFilter
 * will include all TAA families. The family-level filtering happens via
 * familyId prefix matching in the sub-test specific blueprint generation.
 *
 * Taalverzorging TAA families: TAA-ONT-*, TAA-SOORT-*
 * Begrijpend Lezen TAA families: TAA-LEZEN-*, TAA-WOORD-*, TAA-SIGNAALWOORD,
 *   TAA-ARGUMENTATIE, TAA-TEKSTSOORT
 */
export const BEGRIJPEND_LEZEN_FAMILY_PREFIXES = [
  'TAA-LEZEN-',
  'TAA-WOORD-',
  'TAA-SIGNAALWOORD',
  'TAA-ARGUMENTATIE',
  'TAA-TEKSTSOORT',
]

export const TAALVERZORGING_TAA_FAMILY_PREFIXES = ['TAA-ONT-', 'TAA-SOORT-']

// ── Sub-Test Definitions per Domain per Group ───────────────────────────────

/** Domain display metadata */
interface DomainMeta {
  id: AssessmentDomainId
  label: string
  shortLabel: string
  description: string
  icon: string
  color: string
  bgColor: string
  borderColor: string
}

export const DOMAIN_META: Record<AssessmentDomainId, DomainMeta> = {
  rekenen: {
    id: 'rekenen',
    label: 'Rekenen-Wiskunde',
    shortLabel: 'Rekenen',
    description: 'Getallen, rekenen, meten, tijd en geld',
    icon: '📐',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  taalverzorging: {
    id: 'taalverzorging',
    label: 'Taalverzorging',
    shortLabel: 'Taal',
    description: 'Spelling, werkwoordspelling en grammatica',
    icon: '✏️',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
  'begrijpend-lezen': {
    id: 'begrijpend-lezen',
    label: 'Begrijpend Lezen',
    shortLabel: 'Lezen',
    description: 'Leesbegrip, woordenschat en tekstbegrip',
    icon: '📖',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
}

/**
 * Get sub-test definition for a domain + group combination.
 * Returns null if the domain is not available for this group.
 */
export function getSubTestDefinition(
  domainId: AssessmentDomainId,
  group: GroupId,
): SubTestDefinition | null {
  const meta = DOMAIN_META[domainId]
  const modules = DOMAIN_MODULE_MAP[domainId]

  switch (domainId) {
    case 'rekenen':
      return {
        domainId,
        label: meta.label,
        modules,
        questionCount: 25,
        estimatedMinutes: 35,
        available: true,
        shortNote: null,
      }

    case 'taalverzorging':
      return {
        domainId,
        label: meta.label,
        modules: ['SpellingRegel', 'SpellingWW', 'Taalbegrip'],
        questionCount: 22,
        estimatedMinutes: 27,
        available: true,
        shortNote: null,
      }

    case 'begrijpend-lezen': {
      // Group 6: only 2 families available (TAA-LEZEN-DETAIL + TAA-WOORD-SYNONIEM)
      // → shortened section, 8 questions
      if (group === 6) {
        return {
          domainId,
          label: 'Taal & Lezen',
          modules: ['Taalbegrip'],
          questionCount: 8,
          estimatedMinutes: 10,
          available: true,
          shortNote: 'Verkorte toets (groep 6)',
        }
      }
      // Group 7: 9 families → 12-15 questions
      if (group === 7) {
        return {
          domainId,
          label: meta.label,
          modules: ['Taalbegrip'],
          questionCount: 15,
          estimatedMinutes: 20,
          available: true,
          shortNote: null,
        }
      }
      // Group 8: 6 families → 12-15 questions
      return {
        domainId,
        label: meta.label,
        modules: ['Taalbegrip'],
        questionCount: 15,
        estimatedMinutes: 20,
        available: true,
        shortNote: null,
      }
    }

    default:
      return null
  }
}

/**
 * Get all available sub-tests for a group, in display order.
 */
export function getAvailableSubTests(group: GroupId): SubTestDefinition[] {
  const domainOrder: AssessmentDomainId[] = [
    'rekenen',
    'taalverzorging',
    'begrijpend-lezen',
  ]

  return domainOrder
    .map((domainId) => getSubTestDefinition(domainId, group))
    .filter((def): def is SubTestDefinition => def !== null && def.available)
}

/**
 * Check if a familyId belongs to the Begrijpend Lezen domain.
 * Used for TAA family filtering since both Taalverzorging and Begrijpend Lezen
 * share the 'Taalbegrip' module.
 */
export function isBegrijpendLezenFamily(familyId: string): boolean {
  return BEGRIJPEND_LEZEN_FAMILY_PREFIXES.some(
    (prefix) => familyId === prefix || familyId.startsWith(prefix),
  )
}

/**
 * Check if a familyId belongs to the Taalverzorging TAA subset.
 */
export function isTaalverzorgingTaaFamily(familyId: string): boolean {
  return TAALVERZORGING_TAA_FAMILY_PREFIXES.some((prefix) =>
    familyId.startsWith(prefix),
  )
}
